import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Instagram as InstagramIcon,
  Launch as LaunchIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { clientApi, supportApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner, NicheChip } from '../components/common';
import {
  formatCurrency,
  formatDate,
  formatRelativeDate,
  getInstagramProfileUrl,
  getInitials,
} from '../utils/helpers';

const statusColors = {
  active: 'success',
  paused: 'warning',
  cancelled: 'error',
};

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar, fetchDashboardData } = useApp();

  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [supportLogs, setSupportLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Dialogs
  const [cancelDialog, setCancelDialog] = useState(false);
  const [supportDialog, setSupportDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Support form
  const [supportForm, setSupportForm] = useState({
    type: 'request',
    subject: '',
    description: '',
  });

  const fetchClient = async () => {
    try {
      const [clientRes, invoicesRes, supportRes] = await Promise.all([
        clientApi.getById(id),
        clientApi.getInvoices(id),
        clientApi.getSupportLogs(id),
      ]);
      setClient(clientRes.data);
      setInvoices(invoicesRes.data);
      setSupportLogs(supportRes.data);
    } catch (error) {
      showSnackbar('Failed to load client', 'error');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePause = async () => {
    try {
      await clientApi.pause(id);
      showSnackbar('Client paused', 'success');
      fetchClient();
      fetchDashboardData();
    } catch (error) {
      showSnackbar('Failed to pause client', 'error');
    }
  };

  const handleReactivate = async () => {
    try {
      await clientApi.reactivate(id);
      showSnackbar('Client reactivated', 'success');
      fetchClient();
      fetchDashboardData();
    } catch (error) {
      showSnackbar('Failed to reactivate client', 'error');
    }
  };

  const handleCancel = async () => {
    try {
      await clientApi.cancel(id, cancelReason);
      showSnackbar('Client cancelled', 'success');
      setCancelDialog(false);
      fetchClient();
      fetchDashboardData();
    } catch (error) {
      showSnackbar('Failed to cancel client', 'error');
    }
  };

  const handleAddSupport = async () => {
    try {
      await supportApi.create({
        client: id,
        ...supportForm,
      });
      showSnackbar('Support log added', 'success');
      setSupportDialog(false);
      setSupportForm({ type: 'request', subject: '', description: '' });
      fetchClient();
    } catch (error) {
      showSnackbar('Failed to add support log', 'error');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading client..." />;
  }

  if (!client) {
    return null;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/clients')}>
          <BackIcon />
        </IconButton>
        <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
          {getInitials(client.businessName)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {client.businessName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {client.ownerName} • {client.city}, {client.stateProvince}
          </Typography>
        </Box>
        <Chip
          label={client.status}
          color={statusColors[client.status]}
          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
        />
        {client.status === 'active' && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<PauseIcon />}
            onClick={handlePause}
          >
            Pause
          </Button>
        )}
        {client.status === 'paused' && (
          <Button
            variant="outlined"
            color="success"
            startIcon={<ResumeIcon />}
            onClick={handleReactivate}
          >
            Reactivate
          </Button>
        )}
        {client.status !== 'cancelled' && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => setCancelDialog(true)}
          >
            Cancel
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Tabs */}
          <Card sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Overview" />
              <Tab label={`Invoices (${invoices.length})`} />
              <Tab label={`Support (${supportLogs.length})`} />
            </Tabs>

            <CardContent>
              {activeTab === 0 && (
                <Grid container spacing={3}>
                  {/* Quick Stats */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2, flex: 1 }}>
                        <Typography variant="caption" color="success.dark">Monthly Rate</Typography>
                        <Typography variant="h5" fontWeight={700} color="success.dark">
                          {formatCurrency(client.monthlyRate)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2, flex: 1 }}>
                        <Typography variant="caption" color="info.dark">Months Active</Typography>
                        <Typography variant="h5" fontWeight={700} color="info.dark">
                          {client.monthsActive || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2, flex: 1 }}>
                        <Typography variant="caption" color="primary.dark">Lifetime Value</Typography>
                        <Typography variant="h5" fontWeight={700} color="primary.dark">
                          {formatCurrency(client.lifetimeValue || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Website Info */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Website Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Website URL</Typography>
                        {client.websiteUrl ? (
                          <Button
                            size="small"
                            startIcon={<LaunchIcon />}
                            onClick={() => window.open(client.websiteUrl, '_blank')}
                          >
                            Visit
                          </Button>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Hosting Provider</Typography>
                        <Typography variant="body2">{client.hostingProvider || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Domain</Typography>
                        <Typography variant="body2">{client.domainName || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Domain Expiry</Typography>
                        <Typography variant="body2">
                          {client.domainExpiry ? formatDate(client.domainExpiry) : '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Billing Info */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Billing Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Billing Start</Typography>
                        <Typography variant="body2">{formatDate(client.billingStartDate)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Billing Day</Typography>
                        <Typography variant="body2">{client.billingDay || 1} of each month</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {client.paymentMethod || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {activeTab === 1 && (
                <Box>
                  {invoices.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No invoices yet
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Invoice #</TableCell>
                            <TableCell>Period</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Due Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoices.map((invoice) => (
                            <TableRow key={invoice._id}>
                              <TableCell>{invoice.invoiceNumber}</TableCell>
                              <TableCell>
                                {formatDate(invoice.periodStart, 'MMM yyyy')}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(invoice.totalAmount)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={invoice.status}
                                  size="small"
                                  color={
                                    invoice.status === 'paid' ? 'success' :
                                    invoice.status === 'overdue' ? 'error' : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setSupportDialog(true)}
                    >
                      Add Support Log
                    </Button>
                  </Box>
                  {supportLogs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No support logs yet
                    </Typography>
                  ) : (
                    <List disablePadding>
                      {supportLogs.map((log) => (
                        <ListItem key={log._id} sx={{ px: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={log.type} size="small" />
                                <Typography variant="body2" fontWeight={600}>
                                  {log.subject}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <>
                                {log.description && <span>{log.description} • </span>}
                                {formatRelativeDate(log.createdAt)}
                                <Chip
                                  label={log.status}
                                  size="small"
                                  sx={{ ml: 1 }}
                                  color={log.status === 'resolved' ? 'success' : 'default'}
                                />
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Contact Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Contact Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2">{client.email || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2">{client.phone || '-'}</Typography>
                </Box>
                {client.instagramHandle && (
                  <Button
                    variant="outlined"
                    startIcon={<InstagramIcon />}
                    onClick={() => window.open(getInstagramProfileUrl(client.instagramHandle), '_blank')}
                    fullWidth
                  >
                    {client.instagramHandle}
                  </Button>
                )}
                {client.niche && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Niche</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <NicheChip niche={client.niche} />
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Notes
              </Typography>
              <Typography variant="body2">
                {client.notes || 'No notes'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Client</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will mark the client as cancelled and stop all future billing.
          </Typography>
          <TextField
            label="Cancellation Reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Why is this client cancelling?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>Keep Active</Button>
          <Button variant="contained" color="error" onClick={handleCancel}>
            Cancel Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Support Dialog */}
      <Dialog open={supportDialog} onClose={() => setSupportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Support Log</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Type"
              value={supportForm.type}
              onChange={(e) => setSupportForm({ ...supportForm, type: e.target.value })}
              fullWidth
            >
              <MenuItem value="request">Request</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="issue">Issue</MenuItem>
              <MenuItem value="call">Call</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField
              label="Subject"
              value={supportForm.subject}
              onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={supportForm.description}
              onChange={(e) => setSupportForm({ ...supportForm, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSupport}>Add Log</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDetail;
